import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import { useNavigate } from 'react-router-dom';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import Box from '@mui/material/Box';
import Radio from '@mui/material/Radio';
import Stack from '@mui/material/Stack';
import Button from '@mui/material/Button';
import Divider from '@mui/material/Divider';
import Typography from '@mui/material/Typography';
import RadioGroup from '@mui/material/RadioGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import { useUser } from '../../../hooks/useUser';
import { useWhere } from '../../../hooks/useWhere';

import { toggleAddress } from '../../../redux/legacy/reducers/ducks/legacy/MainDuck';
import { routeNames } from '../../../util/Constants';
import GoogleAutoCompleteInput from '../GoogleAutoCompleteInput';
import { normalizeDeliveryAddress } from '../../../util/deliveryAddress';

function OrderAddressInput({ onClose, onConfirm, disabled }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { isLoggedIn } = useUser();
  const { setSelectedAddress } = useWhere();

  const {
    selectedAddress: currentAddress,
    addresses,
    user,
  } = useSelector(
    ({ order: orderObj, profile, restaurants, main, address }) => ({
      addresses: profile.getAddressesResponse?.response,
      order: orderObj,
      selectedRestaurant: restaurants.selectedRestaurant,
      selectedAddress: address.selectedAddress,
      user: main.user,
    }),
    shallowEqual
  );

  const normalizedAddresses = useMemo(
    () =>
      Array.isArray(addresses) ? addresses.map(normalizeDeliveryAddress) : [],
    [addresses]
  );

  const onRadioChange = (ev) => {
    const newAddress = normalizedAddresses.find(
      (item) => String(item.id) === ev.target.value
    );

    if (newAddress) {
      const selectedAddress = {
        ...newAddress,
        user_id: user.id,
        is_primary: newAddress.primary,
      };

      setSelectedAddress(selectedAddress);
      if (onConfirm) {
        onConfirm();
        return;
      }
    }

    onClose();
  };

  return (
    <div data-test-id="order-address-input">
      <GoogleAutoCompleteInput
        onAfterSelect={onConfirm ? () => onConfirm() : onClose}
        disabled={disabled}
        helperText="Type your address, then tap a result to confirm it."
      />
      {isLoggedIn && (
        <Box>
          <Stack direction="row" justifyContent="space-between">
            <Typography
              variant="subtitle1"
              sx={{ fontWeight: 600 }}
              mt={2}
              mb={2}
            >
              Your Addresses
            </Typography>
            <Button
              data-test-id="RTZBn-Vfs7DsQMMxzO_4f"
              onClick={() => {
                navigate(routeNames.accountDetailsAddresses);
                dispatch(toggleAddress(false));
              }}
            >
              Edit
            </Button>
          </Stack>

          <RadioGroup
            variant="outlined"
            iconcomponent={ArrowDownwardIcon}
            data-test-id="select-on-address-id-Wrapper"
          >
            {normalizedAddresses.map((option) => (
              <Box key={option.id}>
                <Divider mb={2} />
                <FormControlLabel
                  key={`order-address-${option.id}`}
                  onChange={onRadioChange}
                  value={`${option.id}`}
                  data-test-id={`select-on-address-id-Wrapper-${option.id}`}
                  control={
                    <Radio
                      disabled={disabled}
                      checked={
                        Boolean(currentAddress?.address_id) &&
                        String(option.id) === String(currentAddress?.address_id)
                      }
                    />
                  }
                  name={`${option.address}, ${option.city}, ${option.state} ${option.zipcode}`}
                  label={
                    <Box ml={1} py={1}>
                      <Stack direction="row" spacing={1}>
                        <Typography
                          display="inline"
                          variant="body1"
                          sx={{ fontWeight: 600 }}
                        >
                          {option.address}
                        </Typography>
                        {option.primary && (
                          <Typography
                            variant="body1"
                            color="secondary"
                            sx={{ fontWeight: 600 }}
                          >
                            primary
                          </Typography>
                        )}
                      </Stack>
                      <Typography variant="grayText">
                        {option.city}, {option.state} {option.zipcode}
                      </Typography>
                    </Box>
                  }
                />
              </Box>
            ))}
          </RadioGroup>
        </Box>
      )}
    </div>
  );
}

OrderAddressInput.propTypes = {
  onClose: PropTypes.func,
  onConfirm: PropTypes.func,
  disabled: PropTypes.bool,
};

OrderAddressInput.defaultProps = {
  onClose: () => null,
  onConfirm: null,
  disabled: false,
};

export default OrderAddressInput;
